import { format } from 'date-fns'

import { GetHistoryResponse } from '@img-arena/consoles-api-golf-pricing'
import { Box, Stack, Typography } from '@mui/material'
import { Column, COLUMN_CELL_TYPE } from '@/components/table/base'
import type { TeamRound } from '@/types/dde/teamRound.types'
import splitIntoWords from '@/utils/splitIntoWords/splitIntoWords'

import { OddsType } from '../types'
import { RowJsonDialog } from './RowJsonDialog'

export type OddsDataRow = {
  actionType: string
  player: string
  round?: number
  hole?: number
  stroke?: number
  time: string
  odds: OddsType
  difference: number | undefined
  players: OddsType[]
  dlSourceKey: string
  rawActionData?: unknown
}

export const ODDS_DATA_WINNER_TABLE_COLUMNS: Column<OddsDataRow>[] = [
  {
    type: COLUMN_CELL_TYPE.COMPONENT,
    id: '$json',
    label: 'JSON',
    render: (row) => <RowJsonDialog row={row} />
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'actionType',
    label: 'Action Type'
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'player',
    label: 'Player'
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'round',
    label: 'Round',
    transform: (value: number | undefined) =>
      value !== undefined && value !== null ? String(value) : '-'
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'hole',
    label: 'Hole',
    transform: (value: number | undefined) =>
      value !== undefined && value !== null ? String(value) : '-'
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'stroke',
    label: 'Stroke',
    transform: (value: number | undefined) =>
      value !== undefined && value !== null ? String(value) : '-'
  },
  {
    type: COLUMN_CELL_TYPE.COMPONENT,
    id: 'time',
    label: 'Time',
    sortAttribute: 'time',
    sx: {
      width: '10%',
      backgroundColor: ({ palette }) => (palette.mode === 'light' ? '#efefef' : '#333')
    },
    render: (time) => {
      const date = new Date(time)

      return (
        <Stack>
          <Typography fontSize={12} fontWeight="bold">
            {format(date, 'hh:mm:ss a')}
          </Typography>
          <Typography fontSize={12}>{format(date, 'MMM dd, yyyy')}</Typography>
        </Stack>
      )
    }
  },
  {
    type: COLUMN_CELL_TYPE.COMPONENT,
    id: '$odds',
    label: '',
    sx: { paddingX: 0, width: '7%' },
    render: () => (
      <Stack
        flexDirection="column"
        height="100%"
        padding={1.25}
        gap={0.5}
        sx={{ backgroundColor: ({ palette }) => (palette.mode === 'light' ? '#efefef' : '#333') }}
      >
        <Typography fontSize={10} fontWeight="bold" color="text.secondary">
          DL TRADING
        </Typography>
        <Typography fontSize={10} fontWeight="bold" color="text.secondary">
          MODELED
        </Typography>
      </Stack>
    )
  },
  {
    type: COLUMN_CELL_TYPE.COMPONENT,
    id: 'odds',
    label: 'Odds',
    render: (value) => (
      <Stack flexDirection="column" alignItems="start">
        <Typography>{value.dlTradingOdds}</Typography>
        <Typography>{value.modelledOdds}</Typography>
      </Stack>
    )
  },
  {
    type: COLUMN_CELL_TYPE.COMPONENT,
    id: 'difference',
    label: 'Difference',

    sx: {
      backgroundColor: ({ palette }) => (palette.mode === 'light' ? '#efefef' : '#333'),
      boxShadow: 'inset 3px 0 3px -2px rgba(0, 0, 0, 0.2), 3px 0 3px -2px rgba(0, 0, 0, 0.1)',
      width: '10%'
    },
    render: (value) => (
      <Box flexDirection="column" height="100%" padding={1.75}>
        {value !== undefined ? `${value.toFixed(1)}%` : '-'}
      </Box>
    )
  }
]

export const generateThreeBallTableColumns = (playerNames: string[]): Column<OddsDataRow>[] => {
  const columns = [
    {
      type: COLUMN_CELL_TYPE.COMPONENT,
      id: '$json',
      label: 'JSON',
      render: (row) => <RowJsonDialog row={row} />
    },
    {
      type: COLUMN_CELL_TYPE.TEXT,
      id: 'actionType',
      label: 'Action Type'
    },
    {
      type: COLUMN_CELL_TYPE.TEXT,
      id: 'player',
      label: 'Player'
    },
    {
      type: COLUMN_CELL_TYPE.TEXT,
      id: 'round',
      label: 'Round',
      transform: (value: number | undefined) =>
        value !== undefined && value !== null ? String(value) : '-'
    },
    {
      type: COLUMN_CELL_TYPE.TEXT,
      id: 'hole',
      label: 'Hole',
      transform: (value: number | undefined) =>
        value !== undefined && value !== null ? String(value) : '-'
    },
    {
      type: COLUMN_CELL_TYPE.TEXT,
      id: 'stroke',
      label: 'Stroke',
      transform: (value: number | undefined) =>
        value !== undefined && value !== null ? String(value) : '-'
    },
    {
      type: COLUMN_CELL_TYPE.COMPONENT,
      id: 'time',
      label: 'Time',
      sortAttribute: 'time',
      sx: {
        width: '10%',
        backgroundColor: ({ palette }) => (palette.mode === 'light' ? '#efefef' : '#333')
      },
      render: (time) => {
        const date = new Date(time)

        return (
          <Stack>
            <Typography fontSize={12} fontWeight="bold">
              {format(date, 'hh:mm:ss a')}
            </Typography>
            <Typography fontSize={12}>{format(date, 'MMM dd, yyyy')}</Typography>
          </Stack>
        )
      }
    },
    {
      type: COLUMN_CELL_TYPE.COMPONENT,
      id: '$odds',
      label: '',
      sx: { paddingX: 0, width: '7%' },
      render: () => (
        <Stack
          flexDirection="column"
          height="100%"
          padding={1.25}
          gap={0.5}
          sx={{ backgroundColor: ({ palette }) => (palette.mode === 'light' ? '#efefef' : '#333') }}
        >
          <Typography fontSize={10} fontWeight="bold" color="text.secondary">
            DL TRADING
          </Typography>
          <Typography fontSize={10} fontWeight="bold" color="text.secondary">
            MODELED
          </Typography>
        </Stack>
      )
    }
  ] as Column<OddsDataRow>[]

  playerNames.map((playerName, index) => {
    columns.push({
      type: COLUMN_CELL_TYPE.COMPONENT,
      id: '$players',
      label: playerName,
      sx: { width: '15%' },
      render: (row: OddsDataRow) => (
        <Stack flexDirection="column" alignItems="start">
          <Typography>{row.players[index]?.dlTradingOdds}</Typography>
          <Typography>{row.players[index]?.modelledOdds}</Typography>
        </Stack>
      )
    } as unknown as Column<OddsDataRow>)
  })
  columns.push({
    type: COLUMN_CELL_TYPE.COMPONENT,
    id: 'difference',
    label: 'Difference',

    sx: {
      backgroundColor: ({ palette }) => (palette.mode === 'light' ? '#efefef' : '#333'),
      boxShadow: 'inset 3px 0 3px -2px rgba(0, 0, 0, 0.2), 3px 0 3px -2px rgba(0, 0, 0, 0.1)',
      width: '10%'
    },
    render: (value) => (
      <Box flexDirection="column" height="100%" padding={1.75}>
        {value !== undefined ? `${value.toFixed(1)}%` : '-'}
      </Box>
    )
  })

  return columns
}

export const getOddsDataWinnerTableRows = (
  data: GetHistoryResponse['oddsEntries']
): OddsDataRow[] => {
  return data.map((oddsEntry) => {
    const modelledOdds = oddsEntry.players[0].playerOdds.modelledOdds
      ? oddsEntry.players[0].playerOdds.modelledOdds
      : '-'
    const hasModelledOdds = modelledOdds !== '-'

    return {
      dlSourceKey: oddsEntry.dlSourceKey,
      actionType: 'Odds Update',
      player: oddsEntry.players[0].playerFullName,
      round: undefined,
      hole: undefined,
      stroke: undefined,
      time: oddsEntry.timestamp,
      odds: {
        dlTradingOdds: oddsEntry.players[0].playerOdds.dlTradingOdds,
        modelledOdds
      },
      difference:
        hasModelledOdds && oddsEntry.oddsPercentageDifference
          ? oddsEntry.oddsPercentageDifference
          : undefined,
      players: oddsEntry.players.map((player) => ({
        dlTradingOdds: player.playerOdds.dlTradingOdds,
        modelledOdds: player.playerOdds.modelledOdds ? player.playerOdds.modelledOdds : '-'
      }))
    }
  })
}

export const getOddsDataThreeBallTableRows = (
  data: GetHistoryResponse['oddsEntries']
): OddsDataRow[] => {
  return data.map((oddsEntry) => {
    const hasAnyModelledOdds = oddsEntry.players.some(
      (player) =>
        player.playerOdds.modelledOdds !== null && player.playerOdds.modelledOdds !== undefined
    )

    return {
      dlSourceKey: oddsEntry.dlSourceKey,
      actionType: 'Odds Update',
      player: '',
      round: undefined,
      hole: undefined,
      stroke: undefined,
      time: oddsEntry.timestamp,
      odds: {
        dlTradingOdds: oddsEntry.players[0].playerOdds.dlTradingOdds,
        modelledOdds: oddsEntry.players[0].playerOdds.modelledOdds
          ? oddsEntry.players[0].playerOdds.modelledOdds
          : '-'
      },
      difference:
        hasAnyModelledOdds && oddsEntry.oddsPercentageDifference
          ? oddsEntry.oddsPercentageDifference
          : undefined,
      players: oddsEntry.players.map((player) => ({
        dlTradingOdds: player.playerOdds.dlTradingOdds,
        modelledOdds: player.playerOdds.modelledOdds ? player.playerOdds.modelledOdds : '-'
      }))
    }
  })
}

const getActionTypeFromEventElementType = (
  teamRound: TeamRound & { timestamp?: string }
): string => {
  const { eventElementType } = teamRound
  if (!eventElementType) return 'Unknown'

  switch (eventElementType) {
    case 'RoundStatusUpdate': {
      if ('state' in teamRound && teamRound.state && typeof teamRound.state === 'string') {
        return `${splitIntoWords(eventElementType)} / ${splitIntoWords(teamRound.state)}`
      }
      return splitIntoWords(eventElementType) || eventElementType
    }
    case 'Correction': {
      if (
        'eventCorrectedIdentifier' in teamRound &&
        teamRound.eventCorrectedIdentifier &&
        typeof teamRound.eventCorrectedIdentifier === 'object' &&
        teamRound.eventCorrectedIdentifier !== null &&
        'eventElementType' in teamRound.eventCorrectedIdentifier &&
        typeof teamRound.eventCorrectedIdentifier.eventElementType === 'string'
      ) {
        const correctedType = teamRound.eventCorrectedIdentifier.eventElementType
        return `${splitIntoWords(correctedType)} / ${splitIntoWords(eventElementType)}`
      }
      return splitIntoWords(eventElementType) || eventElementType
    }
    case 'ModeChange': {
      if ('mode' in teamRound && teamRound.mode && typeof teamRound.mode === 'string') {
        return `${splitIntoWords(eventElementType)}: ${splitIntoWords(teamRound.mode)}`
      }
      return splitIntoWords(eventElementType) || eventElementType
    }
    case 'ConnectionStateChanged': {
      if ('state' in teamRound && teamRound.state && typeof teamRound.state === 'string') {
        return splitIntoWords(teamRound.state) || teamRound.state
      }
      return splitIntoWords(eventElementType) || eventElementType
    }
    default:
      return splitIntoWords(eventElementType) || eventElementType
  }
}

export const convertTeamRoundToOddsDataRow = (
  teamRound: TeamRound & { timestamp?: string },
  playerName: string
): OddsDataRow => {
  const emptyOdds: OddsType = {
    dlTradingOdds: 0,
    modelledOdds: '-'
  }

  return {
    dlSourceKey: '',
    actionType: getActionTypeFromEventElementType(teamRound),
    player: playerName || teamRound.player?.displayName || '',
    round: teamRound.roundNo,
    hole: teamRound.holeNo,
    stroke: teamRound.strokeNo,
    time: teamRound.timestamp || new Date().toISOString(),
    odds: emptyOdds,
    difference: undefined,
    players: [emptyOdds],
    rawActionData: teamRound
  }
}
